import React, { useRef, useState } from 'react';
import { SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  DatePicker as XDatePicker,
  DatePickerProps as XDatePickerProps,
} from '@mui/x-date-pickers';
import {
  DateSelectorNormalizedStyle,
  PLACEHOLDER_COLOR,
} from '@/components/@IntranetLibrary/DatePicker/styles';
import CalendarIcon from '@/public/icons/calendar.svg';
import { Dayjs } from 'dayjs';

type DatePickerProps = XDatePickerProps<Dayjs> & {
  sx?: SxProps<Theme>;
  placeholder?: string;
  error?: { message?: string };
};

const DatePicker: React.FC<DatePickerProps> = ({
  sx,
  slots,
  placeholder,
  error,
  ...datePickerProps
}) => {
  const [open, setOpen] = useState(false);
  const wasOpenOnPressRef = useRef(false);
  const [uncontrolledValue, setUncontrolledValue] = useState<Dayjs | null>(
    datePickerProps.defaultValue ?? null,
  );

  // 비제어(defaultValue만 전달)로 써도 값 유무를 판단할 수 있도록 추적한다.
  const isEmpty =
    datePickerProps.value !== undefined
      ? !datePickerProps.value
      : !uncontrolledValue;

  return (
    <XDatePicker
      {...datePickerProps}
      // 기본값(@media (pointer: fine))이면 터치 기기에서 팝오버 대신
      // MUI 기본 모달(SELECT DATE/Cancel/OK)이 열린다. 항상 팝오버로 고정한다.
      desktopModeMediaQuery={'@media (min-width: 0px)'}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={(value, context) => {
        setUncontrolledValue(value);
        datePickerProps.onChange?.(value, context);
      }}
      sx={{
        ...DateSelectorNormalizedStyle,
        ...sx,
        // 값이 없을 때는 placeholder 든, 포커스 시 MUI X 가 값 자리에 그려주는
        // 포맷 마스크(YYYY-MM 등)든 모두 placeholder 색으로 보이게 한다.
        ...(isEmpty && {
          '& .MuiInputBase-input': { color: PLACEHOLDER_COLOR },
        }),
      }}
      slots={{
        ...slots,
        openPickerIcon: () => <CalendarIcon width={20} height={20} />,
      }}
      slotProps={{
        textField: {
          helperText: error?.message ?? '',
          error: Boolean(error),
          placeholder,
          // 열린 상태에서 필드를 다시 누르면 MUI X 의 click-away 가 팝오버를
          // 닫는다. 이때 onClick 이 무조건 setOpen(true) 를 하면 닫히자마자 다시
          // 열려 팝오버가 순간 깜빡인다(터치는 click-away 가 touchstart 에서
          // 먼저 돌아 onClick 시점엔 이미 닫힌 상태라 특히 잘 재현된다).
          // 그래서 "누르기 시작한 시점"의 열림 여부로 판단한다.
          onPointerDown: () => {
            wasOpenOnPressRef.current = open;
          },
          onClick: () => {
            if (!wasOpenOnPressRef.current) setOpen(true);
          },
          inputProps: { readOnly: true },
        },
        monthButton: {
          sx: {
            '&.Mui-disabled': {
              color: 'var(--dk-text-disabled, #D1D8E2)',
            },
          },
        },
      }}
    />
  );
};

export default DatePicker;
